<template>
  <div class="modal sell-modal">
    <div class="overlay" @click="$parent.closeSellModal"></div>
    <div class="wrapper">
      <div class="container">
        <div class="form">
          <div class="form-wrapper">
            <img
              class="close"
              @click="$parent.closeSellModal"
              src="./../images/close.svg"
            />
            <div class="form-fields">
              <div class="form-fields-wrapper">
                <div class="title dark">SELL ITEMS</div>
                <div class="sell-modal-items" v-if="$parent.inventoryItems.length">
                  <div class="sell-modal-item-header">
                    <span class="col-name">ITEM</span>
                    <span class="col-available">AVAILABLE</span>
                    <span class="col-qty-controls">QTY</span>
                    <span class="col-price">PRICE</span>
                  </div>
                  <div
                    class="sell-modal-item"
                    v-for="(item, index) in $parent.inventoryItems"
                    :key="index"
                    :class="{ selected: item._quantity > 0 }"
                  >
                    <span class="col-name">{{ item.game_name || (item.catalog_item && item.catalog_item.name) || item.short_name }}</span>
                    <span class="col-available">{{ item.amount }}</span>
                    <div class="col-qty-controls">
                      <button
                        class="qty-btn minus"
                        :disabled="item._quantity <= 0"
                        @click="decrement(item)"
                      >−</button>
                      <input
                        class="qty-value"
                        type="number"
                        min="0"
                        :max="item.amount"
                        v-model.number="item._quantity"
                        @input="clampQuantity(item)"
                      />
                      <button
                        class="qty-btn plus"
                        :disabled="item._quantity >= item.amount"
                        @click="increment(item)"
                      >+</button>
                    </div>
                    <input
                      class="col-price"
                      type="number"
                      min="0.01"
                      step="0.01"
                      v-model.number="item._price"
                      :disabled="item._quantity <= 0"
                      placeholder="0.00"
                    />
                  </div>
                </div>
                <div v-else class="no-items desc dark">
                  No items found in your inventory.
                </div>
                <div class="sell-modal-total" v-if="selectedItems.length">
                  <span>Total:</span>
                  <span class="total-value">{{ totalAmount.toFixed(2) }}</span>
                  <img src="../images/coin.svg" class="coin" />
                </div>
                <div class="chekbox-container">
                  <label class="chekbox-label">
                    <div class="chekbox-label-wrapper">
                      <input v-model="adminApproval" type="checkbox" />
                      <div class="checkbox"></div>
                      <span class="desc dark">
                        I understand this transaction requires admin approval
                      </span>
                    </div>
                  </label>
                </div>
                <button
                  class="button"
                  :disabled="!canSubmit"
                  @click="submitSellRequest"
                >
                  CREATE SELL REQUEST
                </button>
                <transition name="slide">
                  <div v-if="error" class="error-desc desc red">
                    {{ error }}
                  </div>
                </transition>
                <transition name="slide">
                  <div v-if="success" class="success-desc desc green">
                    {{ success }}
                  </div>
                </transition>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "@/axios";

export default {
  name: "SellModal",
  data() {
    return {
      adminApproval: false,
      error: "",
      success: "",
      submitting: false,
    };
  },
  computed: {
    selectedItems() {
      return this.$parent.inventoryItems.filter((item) => item._quantity > 0);
    },
    totalAmount() {
      return this.selectedItems.reduce((sum, item) => {
        return sum + (item._quantity || 0) * (item._price || 0);
      }, 0);
    },
    canSubmit() {
      return (
        this.adminApproval &&
        this.selectedItems.length > 0 &&
        this.selectedItems.every(
          (item) => item._quantity > 0 && item._price > 0
        ) &&
        !this.submitting
      );
    },
  },
  methods: {
    increment(item) {
      if (item._quantity < item.amount) {
        item._quantity++;
      }
    },
    decrement(item) {
      if (item._quantity > 0) {
        item._quantity--;
      }
    },
    clampQuantity(item) {
      let val = parseInt(item._quantity, 10);
      if (isNaN(val) || val < 0) val = 0;
      if (val > item.amount) val = item.amount;
      item._quantity = val;
    },
    submitSellRequest() {
      if (!this.canSubmit) return;
      this.submitting = true;
      this.error = "";
      this.success = "";

      const items = this.selectedItems.map((item) => ({
        short_name: item.short_name,
        quantity: item._quantity,
        price: item._price,
      }));

      axios
        .post("user/sell-requests", { items })
        .then((res) => {
          if (res.data.status === "OK") {
            this.success = "Sell request created successfully!";
            setTimeout(() => {
              this.$parent.closeSellModal();
            }, 1500);
          } else {
            this.error = res.data.message || "Failed to create sell request";
          }
        })
        .catch((err) => {
          if (err.response) {
            if (err.response.status === 422) {
              this.error =
                err.response.data.message || "Validation error. Check your items.";
            } else if (err.response.status === 404) {
              this.error = "Player not found on server. Please join the game first.";
            } else if (err.response.status === 503) {
              this.error = "Game server is currently unavailable. Please try again later.";
            } else {
              this.error =
                err.response.data.message || "Failed to create sell request";
            }
          } else {
            this.error = "Network error. Please try again.";
          }
        })
        .finally(() => {
          this.submitting = false;
        });
    },
  },
};
</script>

<style scoped lang="scss">
.sell-modal {
  .close {
    filter: brightness(0) invert(1);
  }

  .wrapper {
    max-width: 780px;
    max-height: 100%;
    overflow: auto;
  }

  .container {
    padding: 34px 24px;
  }

  .form-fields-wrapper {
    max-width: 720px;
    margin: 0 auto;
    padding-bottom: 16px;
  }

  .title {
    text-align: center;
  }

  .no-items {
    text-align: center;
    margin-top: 24px;
    opacity: 0.7;
  }

  .sell-modal-items {
    max-height: 400px;
    overflow-y: auto;
    margin-top: 24px;
    padding-right: 4px;
  }

  .sell-modal-item-header,
  .sell-modal-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 8px;
  }

  .sell-modal-item-header {
    border-bottom: 1px solid rgba(255, 255, 255, 0.15);
    margin-bottom: 8px;
    padding-top: 0;

    span {
      font-size: 11px;
      font-weight: 600;
      text-transform: uppercase;
      color: rgba(255, 255, 255, 0.5);
      letter-spacing: 0.05em;
    }
  }

  .sell-modal-item {
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);

    &.selected {
      background: rgba(255, 255, 255, 0.04);
      border-radius: 8px;
    }
  }

  .col-name {
    flex: 1;
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .col-available {
    width: 70px;
    text-align: center;
    color: rgba(255, 255, 255, 0.8);
  }

  .col-qty-controls {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    width: 150px;
    flex-shrink: 0;
  }

  .qty-btn {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    border: 1px solid rgba(255, 255, 255, 0.35);
    background: rgba(255, 255, 255, 0.08);
    color: #fff;
    font-size: 18px;
    line-height: 1;
    padding: 0;
  }

  .qty-value {
    width: 64px;
    min-width: 64px;
    text-align: center;
    padding: 10px 8px;
  }

  .col-price,
  input.col-price {
    width: 120px;
    text-align: center;
  }

  .sell-modal-total {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 20px;
    padding: 16px 8px;
    border-top: 1px solid rgba(255, 255, 255, 0.15);

    .coin {
      width: 16px;
      height: 16px;
    }
  }

  .chekbox-container {
    margin-top: 20px;

    .chekbox-label-wrapper {
      display: flex;
      align-items: flex-start;
    }
  }

  .form-fields-wrapper > .button {
    margin: 24px auto 0;
    max-width: 276px;
  }

  .error-desc,
  .success-desc {
    text-align: center;
    margin-top: 12px;
  }
}

@media screen and (max-width: 768px) {
  .sell-modal .wrapper {
    max-width: 100%;
  }

  .sell-modal .container {
    padding: 24px 12px;
  }

  .sell-modal .sell-modal-item-header {
    display: none;
  }

  .sell-modal .sell-modal-item {
    flex-wrap: wrap;
    gap: 8px;
  }

  .sell-modal .col-name {
    width: 100%;
    flex: none;
  }

  .sell-modal .col-available {
    width: auto;
  }

  .sell-modal .col-available::before {
    content: "Avail: ";
    color: rgba(255, 255, 255, 0.5);
  }

  .sell-modal .col-qty-controls {
    width: auto;
  }

  .sell-modal .col-price,
  .sell-modal input.col-price {
    width: 90px;
  }
}
</style>

